
import { Component, Vue } from 'vue-property-decorator'
import AddStyleDialog from '@/components/styles/AddStyleComponent.vue'
import { DELETE_STYLE_MUTATION } from './stylesViewMutations'
import { DeleteStyle, DeleteStyleVariables } from './__generated__/DeleteStyle'
import ProductPackageUploadImportDialog from '@/components/productPackageUploadImportDialog/ProductPackageUploadImportDialog.vue'
import QUERIES from '@/queries/queries'
import { Badkamercore_ProjectType, CrudAction } from '@/__generated__/globalTypes'
import { RouteNames } from '@/router/routeNames'
import {
  DeleteProductPackageMutation,
  DeleteProductPackageMutationVariables,
  DeleteProductPackageDocument
} from '../productPackageListView/gql/__generated__/DeleteProductPackage.mutation'
import {
  StyleFragment,
  StylesViewQueryDocument,
  StylesViewQueryQuery,
  StylesViewQueryQueryVariables
} from './gql/__generated__/StylesViewQuery'
import {
  GetStylePageByAliasQueryDocument,
  GetStylePageByAliasQueryQuery,
  GetStylePageByAliasQueryQueryVariables
} from './gql/__generated__/GetStylePageByAliasQuery'
import CopyStyleDialog from '@/components/projectStyles/CopyStyleDialog.vue'

@Component({
  components: {
    AddStyleDialog,
    CopyStyleDialog,
    ProductPackageUploadImportDialog
  }
})
export default class StylesView extends Vue {
  POLL_INTERVAL = 3000

  headers = [
    {
      text: '',
      value: 'productPackage.isMain',
      sortable: true,
      align: 'center',
      cellClass: 'c-style-listing__icon-column'
    },
    {
      text: '',
      align: 'start',
      sortable: false,
      value: 'name'
    },
    { text: '', value: 'actions', sortable: false, align: 'end' }
  ]

  styles: StyleFragment[] | null = null

  routeNames = RouteNames
  Badkamercore_ProjectType = Badkamercore_ProjectType

  showAddStyleModal = false

  showUpdateProductPackageDialog = false
  styleProductPackageToUpdateId = ''
  styleToUpdateName = ''
  CrudAction = CrudAction

  // Data for copying a style
  showStyleCopyModal = false
  styleToCopyId = ''
  styleToCopyName = ''

  downloadsInProgress: Record<string, boolean> = {}

  created() {
    this.fetchData()
  }

  get isNotAdmin() {
    return !this.$store.getters.isAdmin
  }

  assignRowClasses(item: StyleFragment) {
    return { 'c-style-listing__row': item?.productPackage?.isBeingUpdated }
  }

  fetchData() {
    this.$apollo.addSmartQuery<StylesViewQueryQuery, StylesViewQueryQueryVariables>(QUERIES.StylesViewQuery, {
      query: StylesViewQueryDocument,
      update: (data) => data,
      result: (result) => {
        this.styles = result?.data?.styles
        this.syncPolling()
      },
      error: (error) => {
        this.$store.dispatch('showErrorDialog', {
          Code: 'E4102',
          Message: error
        })
      }
    })
  }

  syncPolling() {
    const isAnyProductPackageBeingUpdated = this.styles?.some((s) => s.productPackage?.isBeingUpdated)

    if (isAnyProductPackageBeingUpdated) {
      this.$apollo.queries[QUERIES.StylesViewQuery].startPolling(this.POLL_INTERVAL)
    } else {
      this.$apollo.queries[QUERIES.StylesViewQuery].stopPolling()
    }
  }

  downloadStyleProductPackageDataFile(productPackage: StyleFragment) {
    this.$set(this.downloadsInProgress, productPackage.id, true)

    this.$fileApi
      .downloadFile(`/productPackageDataFile/${productPackage.productPackageId}`, productPackage.name)
      .catch((error) => {
        this.$store.dispatch('showErrorDialog', {
          Code: 'E4252',
          Message: error
        })
      })
      .finally(() => {
        this.$set(this.downloadsInProgress, productPackage.id, false)
      })
  }

  deleteStyle(item: StyleFragment) {
    if (item.productPackageId) {
      this.$apolloMutate<DeleteProductPackageMutation, DeleteProductPackageMutationVariables>({
        mutation: DeleteProductPackageDocument,
        variables: {
          productPackageId: item.productPackageId
        },
        refetchQueries: [QUERIES.StylesViewQuery],
        error: 'E4103'
      })
    } else {
      this.$apolloMutate<DeleteStyle, DeleteStyleVariables>({
        mutation: DELETE_STYLE_MUTATION,
        variables: {
          styleId: item.id
        },
        refetchQueries: [QUERIES.StylesViewQuery],
        error: 'E4103'
      })
    }
  }

  openUpdateProductPackageDialog(item: StyleFragment) {
    this.showUpdateProductPackageDialog = true
    this.styleProductPackageToUpdateId = item.productPackageId
    this.styleToUpdateName = item.name
  }

  onUpdateFileUploaded() {
    this.showUpdateProductPackageDialog = false
    this.$router.push({
      name: RouteNames.PRODUCT_PACKAGE_IMPORTS,
      params: { propositionType: Badkamercore_ProjectType.DeBadkamerwerelden }
    })
  }

  openStyleCopyModal(item: StyleFragment) {
    this.showStyleCopyModal = true
    this.styleToCopyId = item.id
    this.styleToCopyName = item.name + this.$t('style.copy.form.defaultFieldValue')
  }

  navigateToCms(item: StyleFragment) {
    this.$apollo
      .query<GetStylePageByAliasQueryQuery, GetStylePageByAliasQueryQueryVariables>({
        query: GetStylePageByAliasQueryDocument,
        variables: {
          aliasId: item.id
        }
      })
      .catch((reason) => {
        this.$store.dispatch('showErrorDialog', {
          Code: 'E4104',
          Message: reason
        })
      })
      .then((response) => {
        if (response) {
          const host = process.env.VUE_APP_CMS_URL
          const contentItemId = response.data.stylePage?.[0]?.contentItemId
          if (contentItemId) {
            const url = `${host}/Admin/Contents/ContentItems/${contentItemId}/Edit?returnUrl=%2FAdmin%2FContents%2FContentItems`
            window.open(url, 'editStyleInCms')
          } else {
            this.$store.dispatch('showErrorDialog', {
              Code: 'E4105'
            })
          }
        }
      })
  }

  get styleNames() {
    return this.styles?.map((item) => item.name.replace(/[^a-zA-Z0-9]/g, ''))
  }

  confirmDeleteStyle(item: StyleFragment) {
    this.$showConfirmationDialog({
      awaitConfirm: true,
      Callback: async () => {
        await this.deleteStyle(item)
        this.$hideConfirmationDialog()
      },
      Code: 'C4037'
    })
  }
}
